import type { ApiError } from "./schemas";

/**
 * The error taxonomy the UI is built to handle.
 *
 * Most of these DON'T EXIST in the backend yet — the Lambda today returns only
 * 400/429/502. They are defined now because building the UI against the full set
 * is what makes pass 2 a wiring job instead of a rewrite. Each one maps to a
 * control in sports-ai-platform-plan.md §8A.
 */
export type ApiErrorCode =
  /** 401 — token expired. Silent refresh once, retry once, then login. */
  | "unauthenticated"
  /** 403 — entitlement changed mid-session (sub cancelled/expired). Refetch, then upsell. */
  | "not-entitled"
  /** 403 + `under_18` — the server did the date arithmetic and the answer is no.
      TERMINAL: there is no retry and nothing to buy, and the server wrote
      nothing when it refused. Never route this to the upsell. */
  | "under-age"
  /** 403 + `age_unverified` — we have never checked, so the request is refused
      until they pass the gate. Distinct from `under-age`: this one is fixable
      by finishing onboarding, that one is not fixable at all. */
  | "age-unverified"
  /** 403 + `country_not_supported` — they told us they live somewhere KEV does
      not serve. TERMINAL like `under-age`, and nothing was written. */
  | "country-unsupported"
  /** 402 — the credit gate refused. §6.3 requires a graceful in-chat moment, never a toast. */
  | "insufficient-credits"
  /** 429 — rate limited. Countdown from Retry-After. */
  | "rate-limited"
  /** 409 — per-user concurrency (2 in flight, §7.3). */
  | "too-many-in-flight"
  /** 409 + `saved_league_conflict` — the saved squad on the account moved on
      since this browser last read it, so the write was REFUSED and nothing was
      overwritten. Not an error to retry through: the two copies have to be put
      in front of the reader and one of them chosen. Distinct from the
      concurrency 409 above because the remedies could not be less alike — that
      one is "wait a second", this one is "look at both and decide". */
  | "saved-league-conflict"
  /** 503 — circuit breaker tripped (§8A #9). A DESIGNED state, not a crash. */
  | "circuit-open"
  /** 503 + `model_busy` — Bedrock is at CAPACITY, not broken. Transient: the
      same question a minute later works. Refunded server-side like any other
      model failure, so this costs the user nothing. */
  | "model-busy"
  /** 400 + `invalid_league_settings` — the saved fantasy league in this browser
      is not a shape the server will accept, so it refused the WHOLE turn rather
      than answer with half a league applied. Nothing was charged and nothing was
      written. Fixable by the punter, in the one panel that owns those settings —
      which is why it is not the generic 400. */
  | "league-settings-invalid"
  /** 400 + `league_settings_sport_mismatch` — a league for one code was posted at
      another code's model. Distinct from the above on the server's own reasoning:
      a shape problem is "fix your form", this one is "you sent an NFL league to
      the NBA". Never route it to the generic error panel; the two remedies are
      different sentences. */
  | "league-settings-sport-mismatch"
  /** 502 — the model call failed upstream. */
  | "upstream-failed"
  /** Anything else, including a response that failed schema validation. */
  | "unknown";

export class KevApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  /** 402 payload — what the gate refused and when it resets. */
  readonly balance?: number;
  readonly required?: number;
  readonly resetAt?: string;
  /** 429 — seconds, from the Retry-After header where present. */
  readonly retryAfterSec?: number;

  constructor(
    code: ApiErrorCode,
    status: number,
    message: string,
    extra?: Partial<ApiError> & { retryAfterSec?: number },
  ) {
    super(message);
    this.name = "KevApiError";
    this.code = code;
    this.status = status;
    this.balance = extra?.balance;
    this.required = extra?.required;
    this.resetAt = extra?.resetAt;
    this.retryAfterSec = extra?.retryAfterSec;
  }
}

/** Status → code. The server's `error` string is advisory; the status is the contract. */
export function codeForStatus(status: number, serverError?: string): ApiErrorCode {
  switch (status) {
    case 400:
      /* The ONLY 400s this app can tell apart, and the only ones it should try
         to. Everything else the Lambda refuses with a 400 — `invalid_json`,
         `model_and_message_required`, `message_too_long`, `invalid_intent` — is
         a bug in this client that a punter can do nothing about, and dressing
         one of those up as a remedy would send them somewhere that cannot help.
         These two they CAN clear: the settings are theirs, saved in this
         browser, and the panel points at the control that owns them. */
      if (serverError === "invalid_league_settings") return "league-settings-invalid";
      if (serverError === "league_settings_sport_mismatch") {
        return "league-settings-sport-mismatch";
      }
      return "unknown";
    case 401:
      return "unauthenticated";
    case 402:
      return "insufficient-credits";
    case 403:
      /* Three different 403s. Entitlement is the common one and stays the
         default, but routing an under-18 refusal to the upsell would offer to
         sell them a way past the age gate — so the two age cases are split out
         by the server's own string, on the same reasoning as the 503 pair
         below. */
      if (serverError === "under_18") return "under-age";
      if (serverError === "age_unverified") return "age-unverified";
      if (serverError === "country_not_supported") return "country-unsupported";
      return "not-entitled";
    case 429:
      return "rate-limited";
    case 409:
      /* Two different 409s, split on the server's own string exactly as the two
         403s and the two 503s are. Routing a saved-squad conflict to the
         in-flight message would tell a reader to wait a moment when what is
         actually needed is a choice between two rosters — and the panel that
         asks for that choice would never open. */
      if (serverError === "saved_league_conflict") return "saved-league-conflict";
      return "too-many-in-flight";
    case 502:
      return "upstream-failed";
    case 503:
      /* Two different 503s, and they are not interchangeable. `circuit_open` is
         US throttling the models to control spend; `model_busy` is BEDROCK
         throttling us. Same status, same refund, different sentence — and only
         the second one is honestly "try again in a minute". */
      if (serverError === "circuit_open") return "circuit-open";
      if (serverError === "model_busy") return "model-busy";
      return "unknown";
    default:
      return "unknown";
  }
}

export function isApiError(e: unknown): e is KevApiError {
  return e instanceof KevApiError;
}
